import { Inject, Injectable } from '@angular/core';
import { BaseAPIService } from '../../base-api.service';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ParameterTypesEnum } from '../enums/parameter-types.enum';
import { BehaviorSubject } from 'rxjs';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { StorageConst } from '../consts/storage-consts';
import { LanguagesEnum } from '../enums/languages.enum';
import { environment } from '../../../environments/environment';
import moment from 'moment';
import { locales } from '../custom-components/custom-form/custom-calendar/locale.const';

interface IParameter {
  data: any;
  name: string;
  parameterId: number;
  shortName: string;
}

interface IDirection {
  direction: 'ltr' | 'rtl';
  side: 'left' | 'right';
}

@Injectable({
  providedIn: 'root'
})
export class UiService {

  private _dateFormatNumber: number;
  private _isRTL: boolean;
  private _currencyChar = '$';


  parametersMap = new Map<number, IParameter>();

  private readonly headerVisible = new BehaviorSubject<boolean>(true);
  public headerVisible$ = this.headerVisible.asObservable();

  private readonly autoLogin = new BehaviorSubject<boolean>(false);
  public autoLogin$ = this.autoLogin.asObservable();

  private readonly parameterSource = new BehaviorSubject<IParameter[]>([]);

  constructor(private http: BaseAPIService, @Inject(DOCUMENT) private document: Document) {
    this.checkDirection();

    this.autoLogin$.subscribe(v => this.headerVisible.next(!v));
  }

  setAutoLogin(value: boolean): void {
    this.autoLogin.next(value);
  }

  setHeaderVisible(value: boolean): void {
    this.headerVisible.next(value);
  }


  async loadParameters() {
    return this.http.get(`api/sys/parameters`, new HttpParams())
      .pipe(map((data: any) => data.list))
      .toPromise()
      .then((res: IParameter[]) => {
        this.setParameters(res);
      }).catch(err => err);
  }

  setParameters(parameters: IParameter[]) {
    parameters.forEach(parameter => {
      this.parametersMap.set(parameter.parameterId, parameter);
    });
    this.parameterSource.next(parameters);
    this.localeInitializer(this.culture);
    this.registerDateFormat();
    this.currencyChar = this.getParameterDataAsString(ParameterTypesEnum.CURRENCY_CHAR);

  }

  set dateFormatNumber(value: number) {
    this._dateFormatNumber = value;
  }

  set isRTL(value: boolean) {
    this._isRTL = value;
    this.setDirectionClassOnBody();
  }

  get dateFormatNumber(): number {
    return this._dateFormatNumber;
  }

  get parameters() {
    return this.parameterSource.getValue();
  }

  get isRTL() {
    return this._isRTL;
  }

  getParameterDataAsNumber(parameterEnum: ParameterTypesEnum): number {
    return this.parametersMap.get(parameterEnum) && Number(this.parametersMap.get(parameterEnum) && this.parametersMap.get(parameterEnum).data);
  }

  getParameterDataAsString(parameterEnum: ParameterTypesEnum): string {
    return this.parametersMap.get(parameterEnum) && String(this.parametersMap.get(parameterEnum).data);
  }

  get culture() {
    return this.getParameterDataAsString(ParameterTypesEnum.CULTURE) || 'en-US';
  }

  get locale() {
    return this.culture.substring(0, this.culture.indexOf('-'));
  }

  getLocalDateFormat() {
     const locale = locales[this.locale];
    let dateFormat = "dd/MM/yyyy";
    dateFormat = locale.dateFormat.split('.').join(this.getParameterDataAsString(ParameterTypesEnum.DATE_SEPARATOR));
    dateFormat = locale.dateFormat.split('/').join(this.getParameterDataAsString(ParameterTypesEnum.DATE_SEPARATOR));
    return dateFormat;
  }
  getLocale() {
    const loc = locales[this.locale];
    return loc;
  }
  localeInitializer(culture: string): Promise<any> {
    const localeId = culture.substring(0, culture.indexOf('-'));
    return import(
      /* webpackInclude: /(bg|he|de|en|es|fr|it|nl|no|pl|pt-BR|pt|fi|sv|ko|ru|zh|zh-Hans|zh-Hant|ja)\.mjs$/ */
      /* webpackMode: "lazy-once" */
      /* webpackChunkName: "i18n-base" */
      `/node_modules/@angular/common/locales/${localeId}`).then(module => {
        registerLocaleData(module.default);
    })
      .catch(err => {
        this.localeInitializer('en-US');
      });
  }

  private registerDateFormat() {
    this.dateFormatNumber = this.getParameterDataAsNumber(ParameterTypesEnum.DATE_FORMAT);
  }

  checkDirection() {
    const user = JSON.parse(sessionStorage.getItem(StorageConst.USER));
    this.isRTL = user && (user.languageId === LanguagesEnum.Arabic || user.languageId === LanguagesEnum.Hebrew);
  }

  directionObject(): IDirection {
    if (this.isRTL) {
      return {
        direction: 'rtl',
        side: 'right'
      };
    }
    return {
      direction: 'ltr',
      side: 'left'
    };
  }

  setDirectionClassOnBody() {
    this.resetDirectionOnBody();
    const dirObj = this.directionObject();
    this.document.body.dir = dirObj.direction;
    this.document.body.classList.add(dirObj.direction.toUpperCase());
  }


  set currencyChar(currencyChar: string) {
    this._currencyChar = currencyChar;
  }

  get currencyChar() {
    return this._currencyChar;
  }

  resetDirectionOnBody() {
    this.document.body.removeAttribute('dir');
    this.document.body.classList.remove('LTR', 'RTL');
  }

  getBusinessDate() {
    try {
      const user = JSON.parse(sessionStorage.getItem(StorageConst.USER));
      return moment(user?.businessDate).toDate();
    } catch (e) {
      return new Date();
    }
  }

  clearCache() {
    return new Promise((resolve, reject) => {
      this.http.post('/api/cus/transaction/clearCache', null, new HttpParams(), environment.venditioAPI)
        .toPromise()
        .then((data) => {
          resolve(data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  scrollToFirstErrorPrimeNg(): void {
    document.querySelector('.p-inputtext.ng-invalid')?.scrollIntoView({ behavior: 'smooth' });
  }

  getFortressParameter(): boolean {
    try {
      const parameter = JSON.parse(this.getParameterDataAsString(ParameterTypesEnum.FORTRESS_CONFIGURATION));
      return !!parameter?.enabled;
    } catch (e) {
      return false;
    }
  }

}
